import React, { ReactElement } from "react";
import {
	EWidgets,
	ICustomFormItemProps,
	IFormFieldProps
} from "@organisms/ZForm/interface";
import {
	capitalizeFirstLetter,
	filterOptionsOnSearch,
	filterSelectByTextInput
} from "@/Contracts/Utils";
import {
	clientMarketArray,
	clientCompanyTypeOptions
} from "@/Contracts/Clients";
import { dropdownPlacements } from "@/Components/Utils";
import moment from "moment";
import ZTypography from "@atoms/ZTypography";
import ZCheckbox from "@atoms/ZCheckbox";
import ZButton from "@atoms/ZButton";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Divider from "antd/lib/divider";
import { handleDeleteShippingAddress, normalizeZipcode } from "@/V2Utils";
import { capitalizeAfterUnderscore, getShippingAddressRowText } from "@/Utils";
import { EIcons } from "@atoms/ZIcon/interface";
import { Address, ShippingAddressFormItems } from "@/Contracts";
import ZMoreButton from "@molecules/ZMoreButton";
import ZMenu from "@molecules/ZMenu";
import { Col, Row } from "antd";
import ZTag from "@atoms/ZTag";
import { EColors } from "@atoms/ZColors/interface";
import ZCollapsibleCardContent from "@molecules/ZCollapsibleCardContent";
import ZTooltip from "@atoms/ZTooltip";
import { ClientFormItemsProps } from "./interface";
import {
	transformAddressFieldsForUI,
	getFallbackFieldOrder,
	convertFallbackToLegacyFormat,
	AddressFieldConfig
} from "./addressFieldUtils";

const { ZText, ZSectionHeading } = ZTypography;

export const getClientFormItem = ({
	isDisabled,
	taxRegionsList,
	showAddTaxRegionDrawer,
	hasTaxEditPermission,
	sameBillingAddress,
	setSameBillingAddress,
	customFieldId,
	currentActiveClient,
	isBulkUpdate,
	handleAddShippingAddressDrawer,
	shippingAddresses,
	isMultipleShippingAddressEnabled = true,
	showEditShippingAddressDrawer,
	setShippingAddresses,
	showOtherDetails = true,
	datePattern,
	isDocumentShipAddressEnabled = false,
	required = false,
	orderedAddressFields,
	companyTypeCustomField
}: ClientFormItemsProps): IFormFieldProps | IFormFieldProps[] | null => {
	const strToCompare = isBulkUpdate
		? `client_${customFieldId}`
		: customFieldId;

	const initialValues = (entity: ShippingAddressFormItems) => {
		switch (entity) {
			case ShippingAddressFormItems.Country:
				return currentActiveClient?.ship_to_address?.country;
			case ShippingAddressFormItems.State:
				return currentActiveClient?.ship_to_address?.state;
			case ShippingAddressFormItems.City:
				return currentActiveClient?.ship_to_address?.city;
			case ShippingAddressFormItems.AddressLine1:
				return currentActiveClient?.ship_to_address?.address_line_1;
			case ShippingAddressFormItems.Pincode:
				return currentActiveClient?.ship_to_address?.pincode;
			default:
				return "";
		}
	};

	const clientCompanySelectOptions =
		(companyTypeCustomField?.allowed_dropdown_values?.length ?? 0) > 0
			? companyTypeCustomField!.allowed_dropdown_values!.map(value => ({
					name: value,
					value: value
			  }))
			: clientCompanyTypeOptions;

	const clientMarketSelectOptions = [
		...clientMarketArray,
		...(!!currentActiveClient?.market &&
		!clientMarketArray.includes(currentActiveClient.market)
			? [
					{
						name: capitalizeFirstLetter(currentActiveClient.market),
						value: currentActiveClient.market
					}
			  ]
			: [])
	];

	const getShippingAddressRow = (shippingAddress: Address, index: number) => (
		<Row className="bg-gray-2 h-11 pt-2 pl-2 mb-2">
			<Col span={22} className="pt-1 flex">
				<ZTooltip
					title={getShippingAddressRowText(shippingAddress)}
					placement="bottomLeft"
				>
					<ZText className="inline-block truncate">
						{getShippingAddressRowText(shippingAddress)}
					</ZText>
				</ZTooltip>
				<ZText>
					{shippingAddress?.is_default ? (
						<ZTag color={EColors.BLUE} className="ml-2">
							Default
						</ZTag>
					) : (
						<></>
					)}
				</ZText>
			</Col>
			{!isDisabled && (
				<Col span={2}>
					<ZMoreButton
						className="mr-2"
						menu={
							<ZMenu className="p-1">
								<ZMenu.Item
									key="edit_shipping_address"
									onClick={$event => {
										showEditShippingAddressDrawer &&
											showEditShippingAddressDrawer(
												$event,
												shippingAddress,
												index
											);
									}}
								>
									Edit
								</ZMenu.Item>
								<ZMenu.Item
									className="text-red-6"
									key="delete_shipping_address"
									onClick={() => {
										setShippingAddresses &&
											handleDeleteShippingAddress(
												shippingAddress,
												index,
												setShippingAddresses,
												isDocumentShipAddressEnabled
													? "Note: This will delete the client's shipping address only. All the documents and sales automations using this address as ship to address will not be affected until you change it in document details."
													: ""
											);
									}}
								>
									Delete
								</ZMenu.Item>
							</ZMenu>
						}
					/>
				</Col>
			)}
		</Row>
	);

	/**
	 * Generate address field items using API ordering if available, fallback to hard-coded order
	 */
	const generateAddressFieldItems = (
		addressType: "billing" | "shipping" = "billing"
	): IFormFieldProps[] => {
		let addressFieldConfigs: AddressFieldConfig[] = [];

		if (addressType === "billing") {
			// Fallback to existing hard-coded order (no API data available)
			addressFieldConfigs = convertFallbackToLegacyFormat(
				getFallbackFieldOrder(),
				currentActiveClient,
				required,
				isBulkUpdate || false,
				addressType
			);
		} else {
			if (orderedAddressFields?.length) {
				// Check if API response contains system fields
				const hasSystemFields = orderedAddressFields.some(
					field => field.is_system_field
				);

				if (hasSystemFields) {
					// Use API-provided field order (includes system fields)
					addressFieldConfigs = transformAddressFieldsForUI(
						orderedAddressFields,
						currentActiveClient,
						addressType,
						required,
						isBulkUpdate || false
					);
				} else {
					// API has custom fields but no system fields - combine fallback system fields + API custom fields
					const fallbackSystemFields = convertFallbackToLegacyFormat(
						getFallbackFieldOrder(),
						currentActiveClient,
						required,
						isBulkUpdate || false,
						addressType
					);

					const apiCustomFields = transformAddressFieldsForUI(
						orderedAddressFields.filter(
							field => !field.is_system_field
						),
						currentActiveClient,
						addressType,
						required,
						isBulkUpdate || false
					);

					// Combine: system fields first, then custom fields
					addressFieldConfigs = [
						...fallbackSystemFields,
						...apiCustomFields
					];
				}
			} else {
				// Fallback to existing hard-coded order (no API data available)
				addressFieldConfigs = convertFallbackToLegacyFormat(
					getFallbackFieldOrder(),
					currentActiveClient,
					required,
					isBulkUpdate || false,
					addressType
				);
			}
		}

		// Convert AddressFieldConfig to IFormFieldProps
		const formFieldProps = addressFieldConfigs.map(fieldConfig => ({
			key: fieldConfig.key,
			label: fieldConfig.label,
			hasFeedback: true,
			required: fieldConfig.isCustomField
				? // For custom fields, respect their mandatory_field_details.for_ui setting
				  orderedAddressFields?.find(f => f.id === fieldConfig.id)
						?.mandatory_field_details?.for_ui || false
				: // For system fields, use the existing logic
				  isBulkUpdate || required,
			message: `Please enter ${fieldConfig.label.toLowerCase()}`,
			widget: fieldConfig.widget,
			initialValue: fieldConfig.initialValue,
			normalize: fieldConfig.normalize,
			widgetProps: fieldConfig.widgetProps
		}));

		return formFieldProps;
	};

	switch (strToCompare) {
		case "client_phone": {
			return {
				key: "phone",
				label: "Phone",
				hasFeedback: true,
				initialValue: currentActiveClient?.billing_address?.phone,
				required: isBulkUpdate || required,
				message: "Please enter phone number",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					type: "text",
					placeholder: "Please enter phone number"
				}
			};
		}
		case "client_fax": {
			return {
				key: "fax",
				label: "Fax",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please enter fax number",
				initialValue: currentActiveClient?.fax,
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					type: "text",
					placeholder: "Please enter fax number"
				}
			};
		}
		case "client_website": {
			return {
				key: "website",
				label: "Website",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please enter website",
				initialValue: currentActiveClient?.website,
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter website"
				}
			};
		}
		case "client_linked_in_url": {
			return {
				key: "linked_in_url",
				label: "LinkedIn URL",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please enter linkedin url",
				initialValue: currentActiveClient?.linked_in_url || "",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter linkedin url"
				}
			};
		}
		case "client_facebook_url": {
			return {
				key: "facebook_url",
				label: "Facebook URL",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please enter facebook url",
				initialValue: currentActiveClient?.facebook_url || "",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter facebook url"
				}
			};
		}
		case "client_twitter_url": {
			return {
				key: "twitter_url",
				label: "Twitter URL",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please enter twitter url",
				initialValue: currentActiveClient?.twitter_url || "",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter twitter url"
				}
			};
		}
		case "client_market": {
			return {
				key: "market",
				label: "Market",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please select market",
				initialValue: currentActiveClient?.market || undefined,
				widget: EWidgets.SELECT,
				widgetProps: {
					showSearch: true,
					allowClear: false,
					placeholder: "Please select market",
					filterOption: filterSelectByTextInput,
					dropdownAlign: dropdownPlacements.bottomLeft,
					getPopupContainer: (trigger: { parentNode: any }) =>
						trigger.parentNode,
					selectOptions: clientMarketSelectOptions,
					"data-testid": "client-market-select"
				}
			};
		}
		case "client_company_types": {
			return {
				key: "company_type",
				label: "Company Type",
				hasFeedback: true,
				required: isBulkUpdate || required,
				message: "Please select company type",
				initialValue: currentActiveClient
					? currentActiveClient.company_type || undefined
					: !isBulkUpdate
					? "Prospect"
					: undefined,
				widget: EWidgets.SELECT,
				widgetProps: {
					showSearch: false,
					allowClear: false,
					placeholder: "Please select company type",
					selectOptions: clientCompanySelectOptions,
					dropdownAlign: dropdownPlacements.bottomLeft,
					getPopupContainer: (trigger: { parentNode: any }) =>
						trigger.parentNode,
					"data-testid": "client-company-type"
				}
			};
		}
		case "client_source": {
			return {
				key: "source",
				label: "Source",
				hasFeedback: true,
				required: isBulkUpdate,
				message: "Please enter source",
				initialValue: currentActiveClient?.source || "",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter source"
				}
			};
		}
		case "client_campaign": {
			return {
				key: "campaign",
				label: "Campaign",
				hasFeedback: true,
				required: isBulkUpdate,
				message: "Please enter campaign",
				initialValue: currentActiveClient?.campaign || "",
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: "Please enter campaign"
				}
			};
		}
		case "client_date_acquired": {
			return {
				key: "date_acquired",
				label: "Date Acquired",
				hasFeedback: true,
				required: isBulkUpdate,
				message: "Please enter date acquired",
				widget: EWidgets.DATEPICKER,
				initialValue: currentActiveClient
					? currentActiveClient.date_acquired
						? moment(currentActiveClient.date_acquired)
						: undefined
					: !isBulkUpdate
					? moment()
					: undefined,
				widgetProps: {
					datePattern,
					className: "w-full",
					allowClear: true,
					placeholder: "Please enter date acquired"
				}
			};
		}
		case "client_tax_region_id": {
			return {
				key: "tax_region_id",
				label: "Tax Region",
				hasFeedback: true,
				required: isBulkUpdate,
				message: "Please select tax region",
				initialValue: !isBulkUpdate
					? currentActiveClient
						? taxRegionsList.find(taxRegion => {
								return (
									currentActiveClient?.tax_region_id ===
										taxRegion.id && !taxRegion.disabled
								);
						  })?.id ?? undefined
						: taxRegionsList.find(taxRegion => {
								return taxRegion.is_default;
						  })?.id ?? undefined
					: undefined,
				widget: EWidgets.SELECT,
				widgetProps: {
					showSearch: true,
					dropdownAlign: dropdownPlacements.bottomLeft,
					getPopupContainer: (trigger: { parentNode: any }) =>
						trigger.parentNode,
					placeholder: "Please select tax region",
					filterOption: filterOptionsOnSearch,
					useThreeLetterSearch: true,
					dropdownRender: (menu: ReactElement) => (
						<div>
							{menu}
							{hasTaxEditPermission && showAddTaxRegionDrawer && (
								<div
									onMouseDown={$event => {
										$event.preventDefault();
									}}
									onClick={showAddTaxRegionDrawer}
									className="py-2 px-3 border-0 border-solid border-t border-gray-5 text-blue-6 cursor-pointer hover:bg-gray-3"
								>
									Add Tax Region
								</div>
							)}
						</div>
					),
					selectOptions: taxRegionsList
						?.filter(taxRegion => !taxRegion.disabled)
						?.map((taxRegion, index) => ({
							key: index,
							value: taxRegion.id,
							"data-testid": `tax-region-${taxRegion.id}`,
							name: capitalizeFirstLetter(taxRegion.name)
						}))
				}
			};
		}
		case "client_billing_address": {
			return [
				{
					key: "billing_address",
					wrapperCol: { span: 24 },
					render: () => (
						<React.Fragment key="billing_address">
							<Divider />
							<ZTypography.ZSectionHeading className="block my-4">
								Billing Address
							</ZTypography.ZSectionHeading>
						</React.Fragment>
					)
				},
				// Use API-based field ordering for billing address
				...generateAddressFieldItems("billing"),
				...(!currentActiveClient
					? [
							{
								key: "use_same_address",
								label: " ",
								widget: ({
									value,
									onChange
								}: ICustomFormItemProps) => (
									<ZCheckbox
										checked={value}
										onChange={(e: CheckboxChangeEvent) => {
											onChange(e.target.checked);
											setSameBillingAddress?.(
												e.target.checked
											);
										}}
										disabled={isDisabled}
									>
										{isMultipleShippingAddressEnabled
											? "Add Billing address to shipping addresses"
											: "Use billing address as shipping address"}
									</ZCheckbox>
								)
							}
					  ]
					: []),
				...(!sameBillingAddress || isMultipleShippingAddressEnabled
					? [
							{
								key: "shipping_address",
								wrapperCol: { span: 24 },
								render: () => (
									<React.Fragment key="shipping_address">
										<Divider />
										<Row className="w-full my-6">
											<Col span={12}>
												<ZSectionHeading className="block">
													Shipping Address
												</ZSectionHeading>
											</Col>
											{isMultipleShippingAddressEnabled && (
												<Col
													span={12}
													className="align-right"
												>
													{!!shippingAddresses?.length && (
														<ZButton
															iconName={
																EIcons.ADD
															}
															className="pl-2 border-0 text-bold"
															onClick={
																handleAddShippingAddressDrawer
															}
															type="primary"
															ghost
															size={"small"}
															iconProps={{
																className:
																	"mr-2"
															}}
															disabled={
																isDisabled
															}
														>
															Add new shipping
															address
														</ZButton>
													)}
												</Col>
											)}
										</Row>
									</React.Fragment>
								)
							},
							...(isMultipleShippingAddressEnabled
								? [
										{
											key: `shipping_addresses`,
											render: () =>
												!shippingAddresses?.length ? (
													<ZButton
														iconName={EIcons.ADD}
														className="bg-gray-2 w-full h-17 text-blue-5 mb-8 text-bold border-0"
														iconButton
														onClick={
															handleAddShippingAddressDrawer
														}
														size={"small"}
														iconProps={{
															className: "mr-2"
														}}
														disabled={isDisabled}
													>
														Add new shipping address
													</ZButton>
												) : (
													<div className="mb-8">
														<ZCollapsibleCardContent
															hideExpand={
																shippingAddresses?.length <=
																5
															}
														>
															{shippingAddresses?.map(
																(
																	shippingAddress,
																	idx
																) =>
																	getShippingAddressRow(
																		shippingAddress,
																		idx
																	)
															)}
														</ZCollapsibleCardContent>
													</div>
												)
										}
								  ]
								: // Use API-based field ordering for shipping address
								  generateAddressFieldItems("shipping"))
					  ]
					: []),
				...(showOtherDetails
					? [
							{
								key: "other_details",
								wrapperCol: { span: 24 },
								render: () => (
									<React.Fragment key="other_detail">
										<Divider />
										<ZSectionHeading className="block my-4">
											Other Details
										</ZSectionHeading>
									</React.Fragment>
								)
							}
					  ]
					: [])
			];
		}
		case "client_country":
		case "client_state":
		case "client_city":
		case "client_address_line_1":
		case "client_address_line_2":
		case "client_pincode": {
			return {
				key: customFieldId as string,
				label:
					customFieldId === "pincode"
						? "Zip Code"
						: capitalizeAfterUnderscore(customFieldId),
				hasFeedback: true,
				required: true,
				message: `Please enter ${
					customFieldId === "pincode"
						? "zip code"
						: customFieldId === "address_line_1"
						? "street"
						: customFieldId === "address_line_2"
						? "street 2"
						: `${customFieldId} name`
				}`,
				widget: EWidgets.INPUT,
				normalize:
					customFieldId === "pincode" ? normalizeZipcode : undefined,
				widgetProps: {
					width: "full",
					placeholder: `Please enter ${
						customFieldId === "pincode"
							? "zip code"
							: customFieldId === "address_line_1"
							? "street"
							: customFieldId === "address_line_2"
							? "street 2"
							: `${customFieldId} name`
					}`
				}
			};
		}
		case "client_ship_to_country":
		case "client_ship_to_state":
		case "client_ship_to_city":
		case "client_ship_to_address_line_1":
		case "client_ship_to_address_line_2":
		case "client_ship_to_pincode": {
			return {
				key: customFieldId as string,
				label: `Shipping ${
					customFieldId === "ship_to_pincode"
						? "Zip Code"
						: capitalizeFirstLetter(customFieldId?.split("_")[2])
				}`,
				hasFeedback: true,
				required: true,
				message: `Please enter shipping ${
					customFieldId === "ship_to_pincode"
						? "zip code"
						: customFieldId === "client_ship_to_address_line_1"
						? "street"
						: customFieldId === "client_ship_to_address_line_2"
						? "street 2"
						: `${customFieldId?.split("_")[2]} name`
				}`,
				normalize:
					customFieldId === "pincode" ? normalizeZipcode : undefined,
				widget: EWidgets.INPUT,
				widgetProps: {
					width: "full",
					placeholder: `Please enter shipping ${
						customFieldId === "ship_to_pincode"
							? "zip code"
							: customFieldId === "client_ship_to_address_line_1"
							? "street"
							: customFieldId === "client_ship_to_address_line_2"
							? "street 2"
							: `${customFieldId?.split("_")[2]} name`
					}`
				}
			};
		}
		default:
			return null;
	}
};
